import {
  DSButton,
  DSChip,
  DSDropdown,
} from "@components/design-system/molecules";
import {
  faFileAlt,
  faSparkles,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTemplates } from "@hooks";
import Twemoji from "@src/Twemoji";
import Lottie from "lottie-react";
import mixpanel from "mixpanel-browser";
import { useFormContext, useWatch } from "react-hook-form";

import ottoScribblesBuild from "../../../../../assets/imgs/otto-scribbles-build.json";
import EmptyState from "../../../../common/loading/EmptyState";
import NoteItem from "../../../../dashboard/dashboardInbox/NoteItem";
import {
  FREEFORM_TEMPLATE_LIBRARY_EXAMPLES,
  SOAP_TEMPLATE_EXAMPLES,
  SOAP_TEMPLATE_LIBRARY_EXAMPLES,
  TEMPLATE_TYPES,
} from "../../../constants";
import { useLoadUserTemplate } from "../../../hooks/useLoadUserTemplate";
import { useTemplateExamples } from "../../../hooks/useTemplateExamples";
import {
  applyBuiltInPreset,
  buildTemplateDropdownOptions,
} from "../../../utils";

export const TranscriptSampleChipMenu = ({
  control,
  handleClearSampleTranscript,
}) => {
  const { setValue } = useFormContext();
  const formData = useWatch({ control });
  const templateUuid = formData.uuid;
  const { freeformExamples } = useTemplateExamples();

  const mapTranscriptChips =
    formData.templateType === TEMPLATE_TYPES.CUSTOM
      ? Object.values(freeformExamples).map((sample) => ({
          name: sample.name,
          sampleTranscript: sample.sampleTranscript,
        }))
      : Object.values(SOAP_TEMPLATE_EXAMPLES).map((sample) => ({
          name: sample.name,
          sampleTranscript: sample.sampleTranscript,
        }));

  return (
    <div className="flex flex-col gap-sm bg-secondary rounded-large p-md">
      <p className="text-sm">
        Select a sample then click <strong>Preview Changes</strong>.
        Your template will be applied to the selected sample
        appointment.
      </p>
      <div className="flex flex-wrap gap-sm">
        {mapTranscriptChips.map((sample) => (
          <DSChip
            key={sample.name}
            variant="selectable"
            applied={
              formData.sampleTranscript === sample.sampleTranscript
            }
            onClick={() => {
              mixpanel.track("Selected Preview Chip", {
                templateUuid,
                templateType: formData.templateType,
                variant: sample.name,
              });
              setValue("sampleTranscript", sample.sampleTranscript, {
                shouldDirty: true,
              });
            }}
            onDismiss={handleClearSampleTranscript}
            dataTestId={`transcript-sample-${sample.name}`}
          >
            {sample.name}
          </DSChip>
        ))}
      </div>
    </div>
  );
};

const TemplateExamplesChipMenu = () => {
  const { setValue } = useFormContext();
  const formData = useWatch();
  const templateUuid = formData.uuid;
  const { ownedTemplates, sharedTemplates } = useTemplates();
  const { freeformExamples, allSoapExamples, allFreeformExamples } =
    useTemplateExamples();
  const { loadUserTemplate, isLoadingTemplate } =
    useLoadUserTemplate();

  async function handleVariantSelect(variant, option) {
    if (option?.isUserTemplate) {
      const result = await loadUserTemplate(variant);
      if (result) {
        setValue("templateFields", result.fields, {
          shouldDirty: true,
        });
        setValue("userPreferences", result.userPreferences);
        setValue("templateVariant", variant);

        mixpanel.track("Selected Template Preset", {
          templateUuid,
          templateType: formData.templateType,
          variant: option.label,
          isUserTemplate: true,
        });
      }
      return;
    }

    mixpanel.track("Selected Template Preset", {
      templateUuid,
      templateType: formData.templateType,
      variant,
      isUserTemplate: false,
    });

    const examples =
      formData.templateType === TEMPLATE_TYPES.CUSTOM
        ? allFreeformExamples
        : allSoapExamples;
    const preset = examples[variant];
    if (!preset) {
      mixpanel.track("Invalid template type", {
        templateType: formData.templateType,
      });
      return;
    }

    applyBuiltInPreset(setValue, preset, formData.templateType);
    setValue("templateVariant", variant);
  }

  const defaultOptions =
    formData.templateType === TEMPLATE_TYPES.SOAP
      ? Object.values(SOAP_TEMPLATE_EXAMPLES).map((t) => ({
          label: t.name,
          value: t.name,
        }))
      : Object.values(freeformExamples).map((t) => ({
          label: t.name,
          value: t.name,
        }));

  const libraryOptions =
    formData.templateType === TEMPLATE_TYPES.SOAP
      ? Object.values(SOAP_TEMPLATE_LIBRARY_EXAMPLES).map((t) => ({
          label: t.name,
          value: t.name,
        }))
      : Object.values(FREEFORM_TEMPLATE_LIBRARY_EXAMPLES).map(
          (t) => ({
            label: t.name,
            value: t.name,
          }),
        );

  const myTemplateOptions = ownedTemplates
    .filter(
      (t) =>
        t.templateType === formData.templateType &&
        t.uuid !== templateUuid,
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((t) => ({
      label: t.name,
      value: t.uuid,
      isUserTemplate: true,
    }));

  const sharedTemplateOptions = sharedTemplates
    .filter((t) => t.templateType === formData.templateType)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((t) => ({
      label: t.name,
      value: t.uuid,
      isUserTemplate: true,
    }));

  const allOptions = buildTemplateDropdownOptions({
    defaultOptions,
    libraryOptions,
    myTemplateOptions,
    sharedTemplateOptions,
  });

  const selectedVariant =
    formData.templateVariant && formData.templateVariant !== "DEFAULT"
      ? formData.templateVariant
      : null;

  const templateLabel =
    formData.templateType === TEMPLATE_TYPES.SOAP
      ? "SOAP"
      : "freeform";

  return (
    <div className="flex items-center justify-between gap-md bg-brand-subtle border border-brand-subtle rounded-large p-md">
      <h4 className="text-sm text-secondary">
        <FontAwesomeIcon
          icon={faSparkles}
          className="text-brand mr-1"
          size="lg"
        />{" "}
        Need inspiration? Start with a{" "}
        <strong>{templateLabel}</strong> template from this list and
        tailor it to your needs. This will not modify the original
        template.
      </h4>
      <DSDropdown
        options={allOptions}
        value={selectedVariant}
        onChange={(value, option) =>
          handleVariantSelect(value, option)
        }
        placeholder={`Select a ${templateLabel} template...`}
        dataTestId="template-preset-dropdown"
        width="w-96"
        maxHeight={400}
        search
        searchPlaceholder="Search templates..."
        disabled={isLoadingTemplate}
        getOptionTestId={(option) =>
          option.isHeader
            ? `template-preset-header-${option.value}`
            : `template-variant-${option.value}`
        }
      />
    </div>
  );
};

export const RealNoteDisplay = ({
  selectedNote,
  onOpenNotePicker,
  showTranscript = true,
}) => {
  if (!selectedNote) {
    return (
      <div className="flex flex-col gap-md bg-secondary rounded-large p-md">
        <p className="text-sm">
          Select a recent note to preview your template against real
          data.
        </p>
        <DSButton
          type="button"
          onClick={onOpenNotePicker}
          icon={faFileAlt}
          dataTestId="open-note-picker-button"
        >
          Choose a Note
        </DSButton>
        <p className="text-xs text-secondary">
          Or switch to &quot;Premade Samples&quot; to use our note
          examples instead.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-md">
      <div className="flex flex-col gap-sm bg-secondary rounded-large p-md">
        <p className="text-sm">
          Click <strong>Preview Changes</strong> to see how your
          template will look against this appointment's transcript.
        </p>
        <NoteItem
          note={selectedNote}
          dashboard={true}
          clickable={false}
          hideNoteStatus
        />
        <div className="flex gap-sm">
          <DSButton
            type="button"
            onClick={onOpenNotePicker}
            dataTestId="change-note-button"
          >
            Change Note
          </DSButton>
        </div>
      </div>
      {showTranscript && (
        <TranscriptPreview>
          {selectedNote.transcription}
        </TranscriptPreview>
      )}
    </div>
  );
};

export const PreviewLoadingState = ({
  previewSource,
  selectedNote,
  sampleTranscript,
}) => (
  <div className="flex flex-row items-center justify-center border-dashed border-2 border-brand bg-secondary rounded-large p-md gap-md">
    <EmptyState
      title="Building your note preview..."
      description="This may take up to a minute. It's hard to do things without opposable thumbs, ya know."
    >
      <Lottie
        animationData={ottoScribblesBuild}
        className="w-[300px]"
        loop={true}
        autoplay={true}
      />
    </EmptyState>
    <div className="font-medium text-xs whitespace-pre-wrap rounded-large p-md text-secondary bg-primary max-w-64">
      {previewSource === "note" && selectedNote ? (
        <>
          <strong className="text-sm">Selected Note:</strong>
          <br />
          <Twemoji emoji={"📝"} /> {selectedNote.title}
          <br />
          {selectedNote.transcription?.substring(0, 500)}...
        </>
      ) : (
        <>
          <strong className="text-sm">Selected Sample:</strong>
          <br />
          {sampleTranscript}
        </>
      )}
    </div>
  </div>
);

export const TranscriptPreview = ({ children, className = "" }) => (
  <div
    className={`font-medium text-xs whitespace-pre-wrap rounded-large p-md text-secondary border-dashed border-2 border-secondary max-h-96 overflow-y-auto ${className}`}
  >
    {children}
  </div>
);

export const PreviewSourceToggle = ({
  previewSource,
  setPreviewSource,
  className = "",
  testIdPrefix = "preview",
}) => (
  <div
    className={`flex gap-xs p-xs bg-secondary rounded-full ${className}`}
  >
    <button
      type="button"
      onClick={() => setPreviewSource("note")}
      className={`flex-1 px-sm py-sm text-sm rounded-full transition-all ${
        previewSource === "note"
          ? "bg-brand text-white shadow-xs font-medium"
          : "text-primary"
      }`}
      data-testid={`${testIdPrefix}-source-note`}
    >
      Use Recent Appointment
    </button>
    <button
      type="button"
      onClick={() => setPreviewSource("sample")}
      className={`flex-1 px-sm py-sm text-sm rounded-full transition-all ${
        previewSource === "sample"
          ? "bg-brand text-white shadow-xs font-medium"
          : "text-primary"
      }`}
      data-testid={`${testIdPrefix}-source-sample`}
    >
      Sample Appointments
    </button>
  </div>
);

export default TemplateExamplesChipMenu;
